import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Receipt, Search, ExternalLink, Download, ChevronUp, ChevronDown, Filter, RotateCcw, Settings, Eye, EyeOff, GripVertical, Trash2, Loader2 } from 'lucide-react';
import { Combobox } from '../components/ui/combobox';
import { Label } from '../components/ui/label';
import { ExportDialog } from '../components/ExportDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { API } from '@/lib/apiBase';
import { filterActiveCategories } from '@/lib/categoryUtils';
import { filterProductsForDynamicLists } from '@/lib/productUtils';

// Column configuration for settings
const ALL_COLUMNS = [
  { key: 'invoice_number', label: 'N° Facture', required: true },
  { key: 'invoice_date', label: 'Date' },
  { key: 'product_name', label: 'Produit' },
  { key: 'supplier_name', label: 'Fournisseur' },
  { key: 'company_name', label: 'Société' },
  { key: 'establishment_name', label: 'Établissement' },
  { key: 'sector_name', label: 'Secteur' },
  { key: 'user_name', label: 'Utilisateur' },
  { key: 'amount_ht', label: 'Montant HT', align: 'right' },
  { key: 'amount_ttc', label: 'Montant TTC', align: 'right' },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.map(c => c.key);
const DEFAULT_COLUMN_ORDER = ALL_COLUMNS.map(c => c.key);

// Format currency with specific currency code
const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(amount || 0);
};

// Format amount with original currency and EUR equivalent if different
const formatAmountWithConversion = (originalAmount, eurAmount, currency = 'EUR') => {
  if (!currency || currency === 'EUR') {
    return formatCurrency(originalAmount, 'EUR');
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span>{formatCurrency(originalAmount, currency)}</span>
      <span className="text-xs text-muted-foreground">({formatCurrency(eurAmount, 'EUR')})</span>
    </span>
  );
};

// Sortable header components
const SortIndicator = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) return <ChevronUp className="w-3 h-3 opacity-30" />;
  return sortDirection === 'asc' 
    ? <ChevronUp className="w-3 h-3" /> 
    : <ChevronDown className="w-3 h-3" />;
};

const SortableHeader = ({ column, sortColumn, sortDirection, onSort, children, className = '' }) => (
  <TableHead 
    className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center gap-1">
      {children}
      <SortIndicator column={column} sortColumn={sortColumn} sortDirection={sortDirection} />
    </div>
  </TableHead>
);

export const Costs = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Column settings
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  // Reference data
  const [companies, setCompanies] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [companyFilter, setCompanyFilter] = useState('');
  const [establishmentFilter, setEstablishmentFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  // Sorting
  const [sortColumn, setSortColumn] = useState('invoice_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lineDeleteTarget, setLineDeleteTarget] = useState(null);
  const [lineDeleteLoading, setLineDeleteLoading] = useState(false);

  const fetchCosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entry-lines/one-off`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCosts(response.data);
    } catch (error) {
      console.error('Error fetching costs:', error);
      toast.error('Erreur lors du chargement des coûts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [companiesRes, sectorsRes, suppliersRes, productsRes, usersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/secteurs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCompanies(companiesRes.data);
      setSectors(sectorsRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
      
      // Extract establishments from companies
      const allEstablishments = [];
      companiesRes.data.forEach(company => {
        const companyEstablishments = company.etablissements || [];
        companyEstablishments.forEach(etab => {
          allEstablishments.push({
            id: etab.id,
            name: etab.name,
            company_id: company.id,
            company_name: company.name
          });
        });
      });
      setEstablishments(allEstablishments);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  }, [token]);

  // Load user column preferences
  const loadColumnPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/preferences/costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = response.data;
      
      if (prefs.visible_columns && prefs.visible_columns.length > 0) {
        // Use exactly what was saved, only filter out invalid columns
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedVisible = prefs.visible_columns.filter(k => allColumnKeys.includes(k));
        setVisibleColumns(savedVisible);
      }
      if (prefs.column_order && prefs.column_order.length > 0) {
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedOrder = prefs.column_order.filter(k => allColumnKeys.includes(k));
        // Add any truly new columns (added to code but not in saved prefs) at the end
        const newColumns = allColumnKeys.filter(k => !prefs.column_order.includes(k));
        setColumnOrder([...savedOrder, ...newColumns]);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    } finally {
      setPrefsLoaded(true);
    }
  }, [token]);

  // Save user column preferences
  const saveColumnPreferences = useCallback(async (visible, order) => {
    try {
      await axios.put(`${API}/user/preferences/costs`, {
        visible_columns: visible,
        column_order: order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  }, [token]);

  // Column settings handlers
  const toggleColumn = (columnKey) => {
    const column = ALL_COLUMNS.find(c => c.key === columnKey);
    if (column?.required) return;
    
    setVisibleColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === columnKey) return;
    
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(columnKey);
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      return newOrder;
    });
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleSaveSettings = async () => {
    await saveColumnPreferences(visibleColumns, columnOrder);
    setSettingsDialogOpen(false);
    toast.success('Préférences sauvegardées');
  };

  const handleResetSettings = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setColumnOrder(DEFAULT_COLUMN_ORDER);
  };

  // Get ordered and visible columns for display
  const displayColumns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => ALL_COLUMNS.find(c => c.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns]);

  // Get cell value for a column
  const getCellValue = (cost, columnKey) => {
    switch (columnKey) {
      case 'invoice_number':
        return <span className="font-medium">{cost.invoice_number}</span>;
      case 'invoice_date':
        return (
          <span className="text-sm">
            {new Date(cost.invoice_date).toLocaleDateString('fr-FR')}
            {cost.currency && cost.currency !== 'EUR' && (
              <span className="ml-1 text-xs text-blue-500 font-medium">({cost.currency})</span>
            )}
          </span>
        );
      case 'product_name':
        return <span className="text-sm">{cost.product_name}</span>;
      case 'supplier_name':
        return <span className="text-sm">{cost.supplier_name}</span>;
      case 'company_name':
        return <span className="text-sm">{cost.company_name}</span>;
      case 'establishment_name':
        return <span className="text-sm">{cost.establishment_name || '-'}</span>;
      case 'sector_name':
        return <span className="text-sm">{cost.sector_name || '-'}</span>;
      case 'user_name':
        return <span className="text-sm">{cost.user_name || '-'}</span>;
      case 'amount_ht':
        return formatAmountWithConversion(cost.amount_ht, cost.amount_ht_eur, cost.currency);
      case 'amount_ttc':
        return <span className="font-medium">{formatAmountWithConversion(cost.amount_ttc, cost.amount_ttc_eur, cost.currency)}</span>;
      default:
        return cost[columnKey] || '-';
    }
  };

  useEffect(() => {
    loadColumnPreferences();
  }, [loadColumnPreferences]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  // Build filter options from data with cascading logic
  const companyOptions = useMemo(() => {
    const unique = [...new Set(costs.map(c => c.company_name).filter(Boolean))];
    return unique.sort().map(c => ({ value: c, label: c }));
  }, [costs]);

  // Establishments: filtered by company if selected
  const establishmentOptions = useMemo(() => {
    let filtered = costs;
    if (companyFilter) {
      filtered = costs.filter(c => c.company_name === companyFilter);
    }
    const unique = [...new Set(filtered.map(c => c.establishment_name).filter(Boolean))];
    return unique.sort().map(e => ({ value: e, label: e }));
  }, [costs, companyFilter]);

  // Sectors: filtered by establishment AND company (cumulative filters)
  const sectorOptions = useMemo(() => {
    let filtered = costs;
    // Apply company filter first
    if (companyFilter) {
      filtered = filtered.filter(c => c.company_name === companyFilter);
    }
    // Then apply establishment filter if selected
    if (establishmentFilter) {
      filtered = filtered.filter(c => c.establishment_name === establishmentFilter);
    }
    const unique = [...new Set(filtered.map(c => c.sector_name).filter(Boolean))];
    return unique.sort().map(s => ({ value: s, label: s }));
  }, [costs, companyFilter, establishmentFilter]);

  // Users: filtered by sector, establishment AND company (cumulative filters)
  const userOptions = useMemo(() => {
    let filtered = costs;
    // Apply company filter first
    if (companyFilter) {
      filtered = filtered.filter(c => c.company_name === companyFilter);
    }
    // Then apply establishment filter if selected
    if (establishmentFilter) {
      filtered = filtered.filter(c => c.establishment_name === establishmentFilter);
    }
    // Then apply sector filter if selected
    if (sectorFilter) {
      filtered = filtered.filter(c => c.sector_name === sectorFilter);
    }
    const unique = [...new Set(filtered.map(c => c.user_name).filter(Boolean))];
    return unique.sort().map(u => ({ value: u, label: u }));
  }, [costs, companyFilter, establishmentFilter, sectorFilter]);

  const supplierOptions = useMemo(() => {
    const unique = [...new Set(costs.map(c => c.supplier_name).filter(Boolean))];
    return unique.sort().map(s => ({ value: s, label: s }));
  }, [costs]);

  // Category options from categories list
  const categoryOptions = useMemo(() => {
    return filterActiveCategories(categories).map(c => ({ value: c.id, label: c.name }));
  }, [categories]);

  // Products: filtered by category if selected (cascade filter)
  const productOptions = useMemo(() => {
    let filtered = filterProductsForDynamicLists(products, categories);
    if (categoryFilter) {
      filtered = filtered.filter(p => p.category_id === categoryFilter);
    }
    return filtered.map(p => ({ value: p.name, label: p.name }));
  }, [products, categories, categoryFilter]);

  // Reset product filter when category changes
  useEffect(() => {
    if (categoryFilter && productFilter) {
      const validProducts = filterProductsForDynamicLists(products, categories)
        .filter(p => p.category_id === categoryFilter)
        .map(p => p.name);
      if (!validProducts.includes(productFilter)) {
        setProductFilter('');
      }
    }
  }, [categoryFilter, productFilter, products, categories]);

  // Reset dependent filters when parent changes
  useEffect(() => {
    // Reset establishment if company changes
    if (companyFilter && establishmentFilter) {
      const validEstablishments = costs
        .filter(c => c.company_name === companyFilter)
        .map(c => c.establishment_name);
      if (!validEstablishments.includes(establishmentFilter)) {
        setEstablishmentFilter('');
      }
    }
  }, [companyFilter, establishmentFilter, costs]);

  useEffect(() => {
    // Reset sector if it's no longer valid for the selected establishment/company
    if (sectorFilter) {
      let validSectors;
      if (establishmentFilter) {
        validSectors = costs
          .filter(c => c.establishment_name === establishmentFilter)
          .map(c => c.sector_name);
      } else if (companyFilter) {
        validSectors = costs
          .filter(c => c.company_name === companyFilter)
          .map(c => c.sector_name);
      } else {
        validSectors = costs.map(c => c.sector_name);
      }
      if (!validSectors.includes(sectorFilter)) {
        setSectorFilter('');
      }
    }
  }, [companyFilter, establishmentFilter, sectorFilter, costs]);

  useEffect(() => {
    // Reset user if it's no longer valid for the selected sector/establishment/company
    if (userFilter) {
      let validUsers;
      if (sectorFilter) {
        validUsers = costs
          .filter(c => c.sector_name === sectorFilter)
          .map(c => c.user_name);
      } else if (establishmentFilter) {
        validUsers = costs
          .filter(c => c.establishment_name === establishmentFilter)
          .map(c => c.user_name);
      } else if (companyFilter) {
        validUsers = costs
          .filter(c => c.company_name === companyFilter)
          .map(c => c.user_name);
      } else {
        validUsers = costs.map(c => c.user_name);
      }
      if (!validUsers.includes(userFilter)) {
        setUserFilter('');
      }
    }
  }, [companyFilter, establishmentFilter, sectorFilter, userFilter, costs]);

  // Check if any filter is active
  const hasActiveFilters = searchTerm || companyFilter || establishmentFilter || sectorFilter || supplierFilter || 
    categoryFilter || productFilter || userFilter || dateFrom || dateTo || amountMin || amountMax;

  // Filter and sort costs
  const filteredAndSortedCosts = useMemo(() => {
    let result = [...costs];
    
    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(cost =>
        cost.product_name?.toLowerCase().includes(search) ||
        cost.company_name?.toLowerCase().includes(search) ||
        cost.establishment_name?.toLowerCase().includes(search) ||
        cost.sector_name?.toLowerCase().includes(search) ||
        cost.supplier_name?.toLowerCase().includes(search) ||
        cost.invoice_number?.toLowerCase().includes(search) ||
        cost.user_name?.toLowerCase().includes(search)
      );
    }
    
    // Apply filters
    if (companyFilter) result = result.filter(c => c.company_name === companyFilter);
    if (establishmentFilter) result = result.filter(c => c.establishment_name === establishmentFilter);
    if (sectorFilter) result = result.filter(c => c.sector_name === sectorFilter);
    if (supplierFilter) result = result.filter(c => c.supplier_name === supplierFilter);
    if (categoryFilter) result = result.filter(c => c.category_id === categoryFilter);
    if (productFilter) result = result.filter(c => c.product_name === productFilter);
    if (userFilter) result = result.filter(c => c.user_name === userFilter);
    if (dateFrom) result = result.filter(c => c.invoice_date >= dateFrom);
    if (dateTo) result = result.filter(c => c.invoice_date <= dateTo);
    if (amountMin) result = result.filter(c => (c.amount_ht || 0) >= parseFloat(amountMin));
    if (amountMax) result = result.filter(c => (c.amount_ht || 0) <= parseFloat(amountMax));
    
    // Sorting
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'invoice_number': aVal = a.invoice_number || ''; bVal = b.invoice_number || ''; break;
        case 'invoice_date': aVal = a.invoice_date || ''; bVal = b.invoice_date || ''; break;
        case 'product_name': aVal = a.product_name || ''; bVal = b.product_name || ''; break;
        case 'supplier_name': aVal = a.supplier_name || ''; bVal = b.supplier_name || ''; break;
        case 'company_name': aVal = a.company_name || ''; bVal = b.company_name || ''; break;
        case 'establishment_name': aVal = a.establishment_name || ''; bVal = b.establishment_name || ''; break;
        case 'sector_name': aVal = a.sector_name || ''; bVal = b.sector_name || ''; break;
        case 'user_name': aVal = a.user_name || ''; bVal = b.user_name || ''; break;
        case 'amount_ht': aVal = a.amount_ht || 0; bVal = b.amount_ht || 0; break;
        case 'amount_ttc': aVal = a.amount_ttc || 0; bVal = b.amount_ttc || 0; break;
        default: aVal = a.invoice_date || ''; bVal = b.invoice_date || '';
      }
      if (typeof aVal === 'string') {
        const compare = aVal.localeCompare(bVal, 'fr');
        return sortDirection === 'asc' ? compare : -compare;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [costs, searchTerm, companyFilter, establishmentFilter, sectorFilter, supplierFilter, categoryFilter, productFilter, userFilter, dateFrom, dateTo, amountMin, amountMax, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCosts.length / itemsPerPage);
  const paginatedCosts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedCosts.slice(start, start + itemsPerPage);
  }, [filteredAndSortedCosts, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchTerm, companyFilter, sectorFilter, supplierFilter, productFilter, userFilter, dateFrom, dateTo, amountMin, amountMax, itemsPerPage]);

  // Calculate totals based on filtered data
  const totals = useMemo(() => ({
    total_ht: filteredAndSortedCosts.reduce((sum, cost) => sum + (cost.amount_ht || 0), 0),
    total_ttc: filteredAndSortedCosts.reduce((sum, cost) => sum + (cost.amount_ttc || 0), 0),
  }), [filteredAndSortedCosts]);

  // Selection functions
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedCosts.length && paginatedCosts.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedCosts.map((c, i) => `${c.entry_id}-${c.id}-${i}`)));
    }
  };

  const toggleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = paginatedCosts.length > 0 && selectedIds.size === paginatedCosts.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < paginatedCosts.length;

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setCompanyFilter('');
    setEstablishmentFilter('');
    setSectorFilter('');
    setSupplierFilter('');
    setCategoryFilter('');
    setProductFilter('');
    setUserFilter('');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleGoToEntry = (entryId) => {
    navigate(`/entries?edit=${entryId}`);
  };

  const confirmDeleteEntryLine = async () => {
    if (!lineDeleteTarget?.entry_id || !lineDeleteTarget?.line_id) return;
    setLineDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${API}/entries/${lineDeleteTarget.entry_id}/lines/${lineDeleteTarget.line_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data?.entry_deleted ? 'Ligne supprimée — l’entrée a été retirée' : 'Ligne supprimée'
      );
      setLineDeleteTarget(null);
      fetchCosts();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Suppression impossible');
    } finally {
      setLineDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="costs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-6 h-6" />
            Coûts ponctuels
          </h1>
          <p className="text-muted-foreground">
            Consultez les coûts ponctuels issus des entrées
            <span className="ml-2 text-sm font-medium text-primary">
              ({filteredAndSortedCosts.length} coût{filteredAndSortedCosts.length > 1 ? 's' : ''} trouvé{filteredAndSortedCosts.length > 1 ? 's' : ''})
              {selectedIds.size > 0 && (
                <span className="text-orange-500"> • {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
              )}
            </span>
          </p>
        </div>
      </div>

      {/* Search, Filter Toggle and Export */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative min-w-[250px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher (produit, société, fournisseur...)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-costs"
            />
          </div>
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres avancés
            {hasActiveFilters && <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setSettingsDialogOpen(true)}
            data-testid="settings-costs-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          <Button variant="outline" onClick={() => setExportDialogOpen(true)}>
            <Download className="w-4 h-4 mr-2" />
            Exporter {selectedIds.size > 0 ? `(${selectedIds.size})` : hasActiveFilters ? `(${filteredAndSortedCosts.length})` : ''}
          </Button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-card rounded-lg border border-border p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-9 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Société</Label>
              <Combobox
                options={companyOptions}
                value={companyFilter}
                onValueChange={setCompanyFilter}
                placeholder="Toutes"
                searchPlaceholder="Rechercher..."
                emptyText="Aucune"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Établissement</Label>
              <Combobox
                options={establishmentOptions}
                value={establishmentFilter}
                onValueChange={setEstablishmentFilter}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText={companyFilter ? "Aucun établissement pour cette société" : "Aucun"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Secteur</Label>
              <Combobox
                options={sectorOptions}
                value={sectorFilter}
                onValueChange={setSectorFilter}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText={establishmentFilter ? "Aucun secteur pour cet établissement" : companyFilter ? "Aucun secteur pour cette société" : "Aucun"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Utilisateur</Label>
              <Combobox
                options={userOptions}
                value={userFilter}
                onValueChange={setUserFilter}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText={sectorFilter ? "Aucun utilisateur pour ce secteur" : establishmentFilter ? "Aucun utilisateur pour cet établissement" : companyFilter ? "Aucun utilisateur pour cette société" : "Aucun"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fournisseur</Label>
              <Combobox
                options={supplierOptions}
                value={supplierFilter}
                onValueChange={setSupplierFilter}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Catégorie</Label>
              <Combobox
                options={categoryOptions}
                value={categoryFilter}
                onValueChange={setCategoryFilter}
                placeholder="Toutes"
                searchPlaceholder="Rechercher..."
                emptyText="Aucune"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Produit</Label>
              <Combobox
                options={productOptions}
                value={productFilter}
                onValueChange={setProductFilter}
                placeholder={categoryFilter ? "Tous" : "Toutes catégories"}
                searchPlaceholder="Rechercher..."
                emptyText={categoryFilter ? "Aucun produit dans cette catégorie" : "Aucun"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date du</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date au</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Montant min (€)</Label>
              <Input
                type="number"
                placeholder="0"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Montant max (€)</Label>
              <Input
                type="number"
                placeholder="∞"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Nombre de coûts</p>
          <p className="text-2xl font-bold">{filteredAndSortedCosts.length}</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total HT</p>
          <p className="text-2xl font-bold">{formatCurrency(totals.total_ht)}</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total TTC</p>
          <p className="text-2xl font-bold text-primary">{formatCurrency(totals.total_ttc)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Tout sélectionner"
                  className={isSomeSelected ? "opacity-50" : ""}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              {displayColumns.map((column) => (
                <SortableHeader 
                  key={column.key}
                  column={column.key} 
                  sortColumn={sortColumn} 
                  sortDirection={sortDirection} 
                  onSort={handleSort}
                  className={column.align === 'right' ? 'text-right' : ''}
                >
                  {column.label}
                </SortableHeader>
              ))}
              <TableHead className={isAdmin ? 'w-28' : 'w-16'}>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCosts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={displayColumns.length + 2}
                  className="text-center py-8 text-muted-foreground"
                >
                  <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun coût ponctuel trouvé</p>
                  {hasActiveFilters && (
                    <Button variant="link" onClick={resetFilters} className="mt-2">
                      Réinitialiser les filtres
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedCosts.map((cost, index) => {
                const rowId = `${cost.entry_id}-${cost.id}-${index}`;
                return (
                  <TableRow 
                    key={rowId}
                    className={selectedIds.has(rowId) ? "bg-primary/5" : ""}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedIds.has(rowId)}
                        onCheckedChange={() => toggleSelectOne(rowId)}
                        aria-label={`Sélectionner ${cost.product_name}`}
                      />
                    </TableCell>
                    {displayColumns.map((column) => (
                      <TableCell 
                        key={column.key}
                        className={column.align === 'right' ? 'text-right' : ''}
                      >
                        {getCellValue(cost, column.key)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="flex items-center gap-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleGoToEntry(cost.entry_id)}
                          title="Voir l'entrée"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        {isAdmin && cost.id ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            title="Supprimer cette ligne (admin)"
                            onClick={() =>
                              setLineDeleteTarget({
                                entry_id: cost.entry_id,
                                line_id: cost.id,
                                label: cost.product_name || cost.invoice_number || 'cette ligne',
                              })
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {filteredAndSortedCosts.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Afficher</span>
            <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>par page</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages || 1}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!lineDeleteTarget} onOpenChange={(o) => !o && setLineDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la ligne ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action retire la ligne « {lineDeleteTarget?.label} » de l’entrée facture. Réservé aux
              administrateurs. Elle est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lineDeleteLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={lineDeleteLoading}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteEntryLine();
              }}
            >
              {lineDeleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="one_off"
        title={selectedIds.size > 0 
          ? `Exporter ${selectedIds.size} coût(s) sélectionné(s)`
          : hasActiveFilters 
            ? `Exporter ${filteredAndSortedCosts.length} coût(s) filtré(s)`
            : "Exporter les coûts ponctuels"
        }
      />

      {/* Settings Dialog for Column Preferences */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres du tableau</DialogTitle>
            <DialogDescription>
              Sélectionnez les colonnes à afficher et réorganisez-les par glisser-déposer
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">Colonnes visibles</Label>
            <div className="space-y-1 border rounded-lg p-2 bg-muted/30">
              {columnOrder.map((columnKey) => {
                const column = ALL_COLUMNS.find(c => c.key === columnKey);
                if (!column) return null;
                const isVisible = visibleColumns.includes(columnKey);
                const isDragging = draggedColumn === columnKey;
                
                return (
                  <div
                    key={columnKey}
                    draggable
                    onDragStart={(e) => handleDragStart(e, columnKey)}
                    onDragOver={(e) => handleDragOver(e, columnKey)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-3 p-2 rounded-md cursor-move
                      ${isDragging ? 'bg-primary/20 opacity-50' : 'hover:bg-muted'}
                      ${!isVisible ? 'opacity-50' : ''}
                      transition-all duration-150
                    `}
                    data-testid={`column-item-${columnKey}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <Checkbox
                      id={`col-${columnKey}`}
                      checked={isVisible}
                      onCheckedChange={() => toggleColumn(columnKey)}
                      disabled={column.required}
                      data-testid={`column-checkbox-${columnKey}`}
                    />
                    <label 
                      htmlFor={`col-${columnKey}`}
                      className="flex-1 text-sm cursor-pointer select-none"
                    >
                      {column.label}
                      {column.required && (
                        <span className="text-xs text-muted-foreground ml-2">(obligatoire)</span>
                      )}
                    </label>
                    {isVisible ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Glissez les éléments pour réorganiser les colonnes
            </p>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetSettings} data-testid="reset-settings-btn">
              Réinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
